import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AppUserStatus,
  AppUserType,
  MerchantStore,
  UserInfo,
} from './MerchantModel';
import { Alert, Badge, Button } from 'reactstrap';
import i18n from '@/utils/i18n';
import { Communicator } from '../../tools/Communication';
import {
  NewUserModal,
  NewUserInfo,
  NewUserModalUserType,
} from '../../components/NewUserModal';
import { EditItem, EditSection } from '../../components/Layout';
import { ContentSection } from '../../components/Layout/ContentSection';
import { LayoutStoreInstance } from '../../LayoutStore';
import { FaCopy, FaExclamationTriangle } from 'react-icons/fa';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';
import { StoreType } from '../../graphql/generatedTypes';

type Brand = {
  id: string;
  external_vytal_id: string;
  permanently_removed: boolean;
  name: string;
};

interface MerchantStorePageProps {
  vytalStoreGuid: string;
  brand: Brand;
  title: string;
  storeType?: StoreType | null;
}

function getInitialStore(vytalStoreGuid: string): MerchantStore {
  return {
    id: vytalStoreGuid,
    merchantId: '',
    externalPickpackId: null,
    name: '',
    appUsers: [],
    storeManagers: [],
    imageIds: [],
    category: { id: 0, nameDe: '-', nameEn: '-' },
    openingHours: [],
    containerInformation: [],
    foodInfoTextDe: '',
    foodInfoTextEn: '',
    phoneNumber: '',
    websiteUrl: '',
    streetAddress: '',
    zipCode: '',
    city: '',
    country: '',
    latitude: 50.9278261,
    longitude: 6.9332776,
    contractedDate: '',
    onboardedDate: '',
    testPeriodEnd: '',
    sellsOfflineCards: false,
    amountOfflineCards: 0,
    region: 0,
    isRemoved: false,
  };
}

function getUserPillLabel(type: AppUserType | undefined): string {
  if (type === 'warehouse_cleaning') {
    return i18n.t('Merchants:warehouseCleaningUserPill');
  }
  if (type === 'carrier_employee') {
    return i18n.t('Merchants:carrierEmployeeUserPill');
  }
  if (type === 'partner_app') {
    return i18n.t('Merchants:partnerAppUserPill');
  }
  return i18n.t('Merchants:unknownUserPill');
}

function getUserPillColor(type: AppUserType | undefined): string {
  if (type === 'warehouse_cleaning') return 'info';
  if (type === 'carrier_employee') return 'success';
  if (type === 'partner_app') return 'primary';
  return 'secondary';
}

function getStatusPillLabel(status: AppUserStatus): string {
  return status === 'Disabled'
    ? i18n.t('Merchants:disabledUserPill')
    : i18n.t('Merchants:blockedUserPill');
}

function getStatusPillTooltip(user: UserInfo): string {
  if (user.status === 'Disabled') {
    return i18n.t('Merchants:disabledUserReason');
  }
  return i18n.t('Merchants:blockedUserReason', {
    attempts: user.accessFailedCount ?? 0,
    until: user.lockoutEnd
      ? new Date(user.lockoutEnd).toLocaleString()
      : i18n.t('Common:unknown'),
  });
}

function UserStatusBadge({ user }: { user: UserInfo }) {
  if (!user.status || user.status === 'Active') return null;

  return (
    <Badge
      color={user.status === 'Disabled' ? 'danger' : 'warning'}
      title={getStatusPillTooltip(user)}
      pill
    >
      {getStatusPillLabel(user.status)}
    </Badge>
  );
}

export function MerchantAppAccountsTab({
  vytalStoreGuid,
  brand,
  title,
  storeType,
}: MerchantStorePageProps) {
  const [store, setStore] = useState<MerchantStore>(() =>
    getInitialStore(vytalStoreGuid),
  );
  const [isNewAppUserModalOpen, setIsNewAppUserModalOpen] = useState(false);
  const [newAppUserErrors, setNewAppUserErrors] = useState<string[] | null>(
    null,
  );
  const [loadingStore, setLoadingStore] = useState(true);

  const allowedUserTypes = useMemo<NewUserModalUserType[]>(
    () =>
      storeType === StoreType.Warehouse
        ? ['partner_app', 'warehouse_cleaning', 'carrier_employee']
        : ['partner_app'],
    [storeType],
  );

  const reloadMerchantStore = useCallback((storeId: string) => {
    setLoadingStore(true);

    Communicator.Get('/Merchant/GetMerchantStore', { storeId })
      .then((response) => response.json())
      .then((storeData: MerchantStore) => {
        if (storeData) {
          setStore(storeData);
          setIsNewAppUserModalOpen(false);
          setLoadingStore(false);
        }
      });
  }, []);

  useEffect(() => {
    reloadMerchantStore(vytalStoreGuid);
  }, [vytalStoreGuid, reloadMerchantStore]);

  const addUser = useCallback(
    (
      url: string,
      userInfo: NewUserInfo,
      onError: (errors: string[]) => void,
    ) => {
      Communicator.PutData(url, {
        username: userInfo.username,
        password: userInfo.password,
        isInternal: userInfo.isInternal,
        storeId: vytalStoreGuid,
      })
        .then((response) => response.json())
        .then(() => {
          reloadMerchantStore(vytalStoreGuid);
        })
        .catch((err) => {
          return err.json().then((data: { errors: string[] }) => {
            onError(data.errors);
          });
        });
    },
    [vytalStoreGuid, reloadMerchantStore],
  );

  const onCreateUser = useCallback(
    (userInfo: NewUserInfo) => {
      const urlByUserType: Record<NewUserModalUserType, string> = {
        partner_app: '/Merchant/AddNewAppUserToMerchantStore',
        warehouse_cleaning:
          '/Merchant/AddNewWarehouseCleaningUserToMerchantStore',
        carrier_employee: '/Merchant/AddNewCarrierEmployeeUserToMerchantStore',
      };
      addUser(urlByUserType[userInfo.userType], userInfo, (ers: string[]) =>
        setNewAppUserErrors(ers),
      );
    },
    [addUser],
  );

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection title={i18n.t('Merchants:partnerAccounts')}>
        <EditItem title={i18n.t('Merchants:partnerAppUsers')}>
          {brand.permanently_removed && (
            <Alert color="warning" className="mb-3">
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                <span>{i18n.t('Merchants:brandRemovedUsersWarning')}</span>
              </div>
            </Alert>
          )}
          {store.isRemoved && (
            <Alert color="warning" className="mb-3">
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                <span>{i18n.t('Merchants:storeRemovedUsersWarning')}</span>
              </div>
            </Alert>
          )}
          <div style={{ marginTop: '1.5em' }}>
            <ul className="list-group" style={{ overflowY: 'auto' }}>
              <Button
                size="sm"
                style={{
                  width: '100%',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
                onClick={() => {
                  setIsNewAppUserModalOpen(true);
                  setNewAppUserErrors(null);
                }}
              >
                {i18n.t('Merchants:addPartnerAppUser')}
              </Button>
              {store.appUsers.length == 0 && !loadingStore ? (
                <p
                  className="list-group-item"
                  style={{ color: '#aaa', textAlign: 'center' }}
                >
                  {i18n.t('Merchants:noPartnerAppUsers')}
                </p>
              ) : null}
              {loadingStore && (
                <p
                  className="list-group-item"
                  style={{ color: '#aaa', textAlign: 'center' }}
                >
                  {i18n.t('Common:loading')}
                </p>
              )}
              {store.appUsers.map((user) => (
                <li
                  key={'store' + user.id}
                  className="list-group-item d-flex justify-content-between"
                >
                  <div className="d-flex align-items-center gap-2">
                    <Badge color={getUserPillColor(user.type)} pill>
                      {getUserPillLabel(user.type)}
                    </Badge>
                    <UserStatusBadge user={user} />
                    <Link to={Routes.users(user.id)}>
                      {`${user.userName}: ${user.password || ''}`}
                    </Link>
                  </div>

                  <div>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(user.userName);
                        LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                      }}
                    >
                      <FaCopy /> Username
                    </Button>{' '}
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(user.password || '');
                        LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                      }}
                    >
                      <FaCopy /> Password
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <NewUserModal
              header={i18n.t('Merchants:addNewAppUser')}
              isOpen={isNewAppUserModalOpen}
              onClose={() => setIsNewAppUserModalOpen(false)}
              errors={newAppUserErrors}
              onCreateUser={onCreateUser}
              doNotAllowInternal={store.category.id !== 10}
              allowedUserTypes={allowedUserTypes}
            />
          </div>
        </EditItem>
      </EditSection>
    </ContentSection>
  );
}
